import React, { useEffect, useState } from 'react';
import { Table, Button, DOMHelper, Stack, Divider, SelectPicker, Checkbox, Tag, Input } from 'rsuite';
import type { SortType } from 'rsuite';
import { BaseErrorModel, PagedResponseModel, PaginationFilterResult } from '@/models/core.model';
import { PointTransaction, PointTransactionPaginationFilter, PointTransactionReason, PointTransactionReasonList, EarnReasons } from '@/models/point-transaction.model';
import { getPagedPointTransactions } from '@/services/point-transaction.service';
import { getUsersLookup } from '@/services/user.service';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import TablePagination from '@/components/TablePagination';
import PageContent from '@/components/PageContent';
import GrantDrawer from './grant-drawer';
import DeductDrawer from './deduct-drawer';
import FetchingSelect from '@/components/FetchingSelect';
import ImageCell from '@/components/ImageCell';
import { jsDateToZone } from '@/utils/date-time';
import { getPointTypesLookup } from '@/services/point-type.service';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

const reasonColorMap: Record<number, string> = {
  [PointTransactionReason.SubscriptionGrant]: 'green',
  [PointTransactionReason.ManualGrant]: 'blue',
  [PointTransactionReason.Expired]: 'red',
  [PointTransactionReason.ManualDeduct]: 'orange',
  [PointTransactionReason.Redeem]: 'violet',
  [PointTransactionReason.MarketplaceSale]: 'cyan',
  [PointTransactionReason.MarketplacePurchase]: 'yellow',
  [PointTransactionReason.AccountGrant]: 'green',
  [PointTransactionReason.AiGenerationRefund]: 'cyan',
  [PointTransactionReason.AiGeneration]: 'violet',
};

interface PointTransactionListProps {
  userId?: number;
}

const PointTransactionList: React.FC<PointTransactionListProps> = ({ userId }) => {
  const [filterReq, setFilterReq] = useState<PointTransactionPaginationFilter>(new PointTransactionPaginationFilter());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<PointTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [openGrantDrawer, setOpenGrantDrawer] = useState(false);
  const [openDeductDrawer, setOpenDeductDrawer] = useState(false);

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: PointTransactionPaginationFilter) => {
    setLoading(true);
    try {
      const finalReq = {
        ...req,
        userId: userId || req.userId,
      };
      const result: PagedResponseModel<PointTransaction> = await getPagedPointTransactions(finalReq);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({
          ...pre,
          pageNumber: result.pageNumber,
          totalRecords: result.totalRecords,
          pageSize: result.pageSize,
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching point transactions: ${errorModel?.errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre,
      orderBy: sortColumn,
      isAscending: sortType == 'asc',
    }));
  };

  const isEarn = (reason: PointTransactionReason) => EarnReasons.includes(reason);

  const handleGranted = () => {
    setOpenGrantDrawer(false);
    fetchData(filterReq);
  };

  const handleDeducted = () => {
    setOpenDeductDrawer(false);
    fetchData(filterReq);
  };

  const formatDate = (date?: Date) => {
    if (!date) return '-';
    return date.toLocaleString();
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Stack spacing={6}>
          <Button appearance="primary" onClick={() => setOpenGrantDrawer(true)}>
            Grant Points
          </Button>
          <Button appearance="primary" color="red" onClick={() => setOpenDeductDrawer(true)}>
            Deduct Points
          </Button>
        </Stack>
        <Stack spacing={6}>
          {!userId && <FetchingSelect
            label="User"
            fetchData={(text, page) => getUsersLookup(text, undefined, page)}
            value={filterReq.userId}
            onChange={dataKey => setFilterReq(pre => ({ ...pre, userId: dataKey }))}
          />}
          <FetchingSelect
            label="Point Type"
            fetchData={(text, page) => getPointTypesLookup(text, page)}
            value={filterReq.pointTypeId}
            onChange={dataKey => setFilterReq(pre => ({ ...pre, pointTypeId: dataKey ?? undefined }))}
          />
          <SelectPicker
            label="Reason"
            data={PointTransactionReasonList}
            searchable={false}
            value={filterReq.reason ?? null}
            onChange={dataKey => setFilterReq(pre => ({ ...pre, reason: dataKey ?? undefined }))}
          />
          <Input 
            placeholder="Keyword" 
            value={filterReq.keyword ?? ''} 
            onChange={dataKey => setFilterReq(pre => ({...pre, keyword: dataKey}))}
          />          
          <Checkbox
            checked={!!filterReq.onlyEarns}
            onChange={(value, checked) => setFilterReq(pre => ({ ...pre, onlyEarns: checked == true ? true : undefined, onlySpends: undefined }))}
            >
            Earns Only
          </Checkbox>
          <Checkbox
            checked={!!filterReq.onlySpends}
            onChange={(value, checked) => setFilterReq(pre => ({ ...pre, onlySpends: checked == true ? true : undefined, onlyEarns: undefined }))}
          >
            Spends Only
          </Checkbox>
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
      >
        {!userId && <Column flexGrow={0.5} align="center">
          <HeaderCell>User Image</HeaderCell>
          <ImageCell dataKey='user.profilePicture'/>
        </Column>}
        {!userId && <Column flexGrow={1} align="left" sortable>
          <HeaderCell>User</HeaderCell>
          <Cell>
            {(rowData: PointTransaction) => rowData.user ? `${rowData.user.firstName} ${rowData.user.lastName}` : `-`}
          </Cell>
        </Column>}
        <Column flexGrow={0.5} align="right" sortable>
          <HeaderCell>Amount</HeaderCell>
          <Cell>
            {(rowData: PointTransaction) => (
              <span style={{ color: isEarn(rowData.reason) ? '#2db85d' : '#e55', fontWeight: 600 }}>
                {isEarn(rowData.reason) ? '+' : ''}{rowData.amount}
              </span>
            )}
          </Cell>
        </Column>
        <Column flexGrow={1} align="center" sortable>
          <HeaderCell>Reason</HeaderCell>
          <Cell>
            {(rowData: PointTransaction) => (
              <Tag color={reasonColorMap[rowData.reason] ?? 'default'}>
                {PointTransactionReasonList.find(r => r.value === rowData.reason)?.label ?? rowData.reason}
              </Tag>
            )}
          </Cell>
        </Column>
        <Column flexGrow={3} align="left">
          <HeaderCell>Note</HeaderCell>
          <Cell dataKey="note" fullText/>
        </Column>
        <Column flexGrow={1} align="center" sortable>
          <HeaderCell>Expire Time</HeaderCell>
          <Cell fullText>
            {(rowData: PointTransaction) => jsDateToZone(rowData.expireTime)}
          </Cell>
        </Column>
        <Column flexGrow={1} align="center" sortable>
          <HeaderCell>Created At</HeaderCell>
          <Cell fullText>
            {(rowData: PointTransaction) => jsDateToZone(rowData.createdAt)}
          </Cell>
        </Column>
        <Column flexGrow={2} align="center" sortable>
          <HeaderCell>Operation ID</HeaderCell>
          <Cell dataKey="operationId" fullText/>
        </Column>        
      </Table>
      <Divider />
      <TablePagination
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({ ...pre, pageNumber: dataKey }))}
        onChangeLimit={dataKey => setFilterReq(pre => ({ ...pre, pageSize: dataKey }))}
      />

      <GrantDrawer open={openGrantDrawer} onClose={() => setOpenGrantDrawer(false)} onSubmitted={handleGranted} userId={userId} />
      <DeductDrawer open={openDeductDrawer} onClose={() => setOpenDeductDrawer(false)} onSubmitted={handleDeducted} userId={userId} />
    </PageContent>
  );
};

export default PointTransactionList;
